import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { RefreshCw, ArrowLeft } from "lucide-react";
import { useLang } from "@/context";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUSES = ["New", "Contacted", "Qualified", "Proposal Sent", "Client", "Closed"];

export default function Crm() {
  const { t } = useLang();
  const [key, setKey] = useState(sessionStorage.getItem("crmKey") || "");
  const [input, setInput] = useState("");
  const [leads, setLeads] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [draftNotes, setDraftNotes] = useState("");

  const load = useCallback(
    async (k) => {
      try {
        const r = await axios.get(`${API}/leads`, { headers: { "X-Admin-Key": k } });
        setLeads(r.data);
        setKey(k);
        sessionStorage.setItem("crmKey", k);
      } catch {
        setLeads(null);
        sessionStorage.removeItem("crmKey");
        setKey("");
      }
    },
    []
  );

  useEffect(() => {
    if (key) load(key);
  }, [key, load]);

  const update = async (id, patch) => {
    try {
      await axios.patch(`${API}/leads/${id}`, patch, { headers: { "X-Admin-Key": key } });
      toast.success(t.crm.saved);
      load(key);
    } catch {
      toast.error(t.contact.error);
    }
  };

  const serviceLabel = (k) => t.contact.services.find((s) => s.key === k)?.label || k || "—";

  if (!key || !leads) {
    return (
      <div data-testid="crm-login" className="min-h-screen bg-ivory flex items-center justify-center px-6">
        <div className="w-full max-w-sm text-center">
          <img src="/brand/logo-mark.png" alt="MH" className="h-14 w-auto mx-auto" />
          <p className="mt-8 text-[11px] uppercase tracking-[0.3em] text-taupe">{t.crm.subtitle}</p>
          <form
            className="mt-8 space-y-5"
            onSubmit={(e) => {
              e.preventDefault();
              load(input);
              setInput("");
            }}
          >
            <input
              data-testid="crm-passcode-input"
              type="password"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={t.crm.passcode}
              className="w-full bg-transparent border-b border-taupe/40 focus:border-gold outline-none py-4 text-base text-charcoal placeholder:text-taupe/70 font-light text-center transition-colors"
            />
            <button
              data-testid="crm-enter-button"
              type="submit"
              className="w-full bg-charcoal text-ivory border border-charcoal py-3.5 text-[11px] uppercase tracking-[0.24em] hover:bg-gold hover:border-gold hover:text-charcoal transition-colors duration-300"
            >
              {t.crm.enter}
            </button>
          </form>
          <Link to="/" className="mt-8 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-taupe hover:text-gold transition-colors">
            <ArrowLeft size={13} /> {t.crm.back}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="crm-dashboard" className="min-h-screen bg-ivory">
      <div className="max-w-6xl mx-auto px-6 sm:px-10 py-12 sm:py-16">
        <div className="flex flex-wrap items-end justify-between gap-6 border-b border-taupe/25 pb-8">
          <div>
            <p className="text-[11px] uppercase tracking-[0.3em] text-gold">{t.crm.subtitle}</p>
            <h1 data-testid="crm-title" className="mt-3 font-serif text-3xl sm:text-4xl text-charcoal font-medium">
              {t.crm.title} <span className="text-taupe">({leads.length})</span>
            </h1>
          </div>
          <div className="flex items-center gap-6">
            <button
              data-testid="crm-refresh-button"
              onClick={() => load(key)}
              className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-taupe hover:text-gold transition-colors"
            >
              <RefreshCw size={13} /> {t.crm.refresh}
            </button>
            <Link to="/" className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-taupe hover:text-gold transition-colors">
              <ArrowLeft size={13} /> {t.crm.back}
            </Link>
          </div>
        </div>

        {leads.length === 0 && (
          <p data-testid="crm-empty" className="py-20 text-center text-taupe font-light">{t.crm.empty}</p>
        )}

        <div>
          {leads.map((lead) => {
            const open = openId === lead.id;
            return (
              <div key={lead.id} data-testid={`crm-lead-${lead.id}`} className="border-b border-taupe/25">
                <button
                  data-testid={`crm-lead-row-${lead.id}`}
                  onClick={() => {
                    setOpenId(open ? null : lead.id);
                    setDraftNotes(lead.notes || "");
                  }}
                  className="w-full grid grid-cols-12 gap-3 items-baseline py-5 text-left group"
                >
                  <span className="col-span-4 sm:col-span-2 text-xs text-taupe">
                    {new Date(lead.created_at).toLocaleDateString()}
                  </span>
                  <span className="col-span-8 sm:col-span-3 font-serif text-lg text-charcoal group-hover:text-gold transition-colors">
                    {lead.name}
                  </span>
                  <span className="col-span-6 sm:col-span-3 text-xs sm:text-sm text-ink/65 font-light">
                    {serviceLabel(lead.service)}
                  </span>
                  <span className="hidden sm:block sm:col-span-2 text-xs text-taupe">{lead.country || "—"}</span>
                  <span className="col-span-6 sm:col-span-2 text-right sm:text-left">
                    <span
                      data-testid={`crm-status-${lead.id}`}
                      className={`inline-block text-[10px] uppercase tracking-[0.18em] px-3 py-1.5 border ${
                        lead.status === "New"
                          ? "border-gold text-gold"
                          : lead.status === "Client"
                          ? "border-charcoal bg-charcoal text-ivory"
                          : "border-taupe/40 text-taupe"
                      }`}
                    >
                      {lead.status}
                    </span>
                  </span>
                </button>

                {open && (
                  <div data-testid={`crm-detail-${lead.id}`} className="pb-8 grid grid-cols-12 gap-y-8 lg:gap-10">
                    <div className="col-span-12 lg:col-span-7 space-y-3 text-sm font-light text-ink/80">
                      <p><span className="text-taupe text-xs uppercase tracking-[0.2em]">Email — </span>{lead.email}</p>
                      <p><span className="text-taupe text-xs uppercase tracking-[0.2em]">{t.contact.form.company} — </span>{lead.company || "—"}</p>
                      <p><span className="text-taupe text-xs uppercase tracking-[0.2em]">{t.contact.form.position} — </span>{lead.position || "—"}</p>
                      <p><span className="text-taupe text-xs uppercase tracking-[0.2em]">{t.contact.form.country} — </span>{lead.country || "—"}</p>
                      <p className="pt-3 border-l-2 border-gold pl-5 text-charcoal leading-relaxed">{lead.message}</p>
                    </div>
                    <div className="col-span-12 lg:col-span-5">
                      <p className="text-[11px] uppercase tracking-[0.24em] text-taupe mb-3">Status</p>
                      <div className="flex flex-wrap gap-2">
                        {STATUSES.map((s) => (
                          <button
                            key={s}
                            data-testid={`crm-set-status-${s.replace(/\s/g, "-")}-${lead.id}`}
                            onClick={() => update(lead.id, { status: s })}
                            className={`text-[10px] uppercase tracking-[0.16em] px-3 py-1.5 border transition-colors duration-300 ${
                              lead.status === s
                                ? "border-gold bg-gold/10 text-charcoal"
                                : "border-taupe/30 text-taupe hover:border-gold hover:text-gold"
                            }`}
                          >
                            {s}
                          </button>
                        ))}
                      </div>
                      <p className="mt-6 text-[11px] uppercase tracking-[0.24em] text-taupe mb-2">{t.crm.notes}</p>
                      <textarea
                        data-testid={`crm-notes-${lead.id}`}
                        rows={3}
                        value={draftNotes}
                        onChange={(e) => setDraftNotes(e.target.value)}
                        className="w-full bg-transparent border border-taupe/30 focus:border-gold outline-none p-3 text-sm font-light text-charcoal resize-none transition-colors"
                      />
                      <button
                        data-testid={`crm-save-notes-${lead.id}`}
                        onClick={() => update(lead.id, { notes: draftNotes })}
                        className="mt-3 border border-charcoal px-6 py-2.5 text-[10px] uppercase tracking-[0.22em] text-charcoal hover:border-gold hover:text-gold transition-colors duration-300"
                      >
                        {t.crm.save}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
